import React, { useState, useEffect, memo, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { MobileMenuButton, MobileMenu } from 'components/artectum/common/mobile-menu';
import ImageWithBaseApi from 'components/common/mediaRender/ImageWithBaseApi';
import { useNavBar } from 'core/context/navBarContext';
import { searchProducts } from 'core/services/api';
import { all_routes } from 'core/data/routes/all_routes';

const Logo = memo(({ svgContent }) => {
    const history = useHistory();

    const handleLogoClick = () => {
        const isAdmin = localStorage.getItem('isAdmin');
        if (isAdmin === 'true') {
            history.push('/admin/content/home-banner-slide');
        } else {
            history.push('/');
        }
    };

    return (
        <div className="site-logo-wrap">
            <div className="site-logo go-top" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                <div className="nav-bar logo" style={{ width: '160px' }} dangerouslySetInnerHTML={{ __html: svgContent }} />
            </div>
        </div>
    );
});

const formatText = (text) => {
    return text.toLowerCase().replace(/[-_]/g, ' ').replace(/^\w/, char => char.toUpperCase());
};

const normalizeText = (text) => {
    return text.toLowerCase().replace(/[-_]/g, ' ').trim();
};

const NavbarArtectum = () => {
    const { blogs, categories, navbarCategories, svgContent } = useNavBar();

    const desiredOrder = [
        "baldosas de concreto",
        "podotáctil",
        "celosías de concreto",
        "baldosas de terrazo",
        "terrazo premezclado",
        "mosaicos de dibujo",
        "fachaletas"
    ];

    const sortedCategories = Array.isArray(categories?.[0]?.options)
        ? categories[0].options.sort((a, b) => {
            const indexA = desiredOrder.indexOf(normalizeText(a.value));
            const indexB = desiredOrder.indexOf(normalizeText(b.value));
            return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
        })
        : [];

    const menuData = [
        {
            title: 'Home',
            link: all_routes.home,
        },
        {
            title: 'Sobre nosotros',
            link: "#",
            subMenuClass: 'sub-menu menu-pages-img-show',
            subMenu: blogs ? [
                { title: 'Acerca de Artectum', link: all_routes.about },
                ...blogs.map(blog => ({
                    title: formatText(blog?.slug),
                    link: `${all_routes.blogDetails}/${blog.slug}`,
                }))
            ] : [],
        },
        {
            title: 'Productos',
            link: all_routes.product,
            subMenuClass: 'mega-menu',
            subMenu: [
                {
                    title: 'Categorias',
                    link: '#',
                    subMenu: sortedCategories.map(category => ({
                        title: formatText(category?.value),
                        link: `${all_routes.product}/${category.value}?categoria_de_productos=${category.value}`,
                    })),
                },
                {
                    title: 'Productos Nuevos',
                    link: '#',
                    subMenu: Array.isArray(navbarCategories?.[0]?.options) ? [
                        ...navbarCategories[0].options.map(category => ({
                            title: formatText(category?.value),
                            link: `${all_routes.product}?product_navbar=${category.value}`,
                        }))
                    ] : [],
                },
            ],
        },
        {
            title: 'Obras terminadas',
            link: all_routes.portfolio,
        },
        {
            title: 'Contacto',
            link: all_routes.contact,
        },
    ];

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const location = useLocation();
    const searchContainerRef = useRef(null);

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const isActive = (item) => {
        if (location.pathname === item.link) {
            return true;
        }
        if (item.subMenu) {
            return item.subMenu.some(subItem => isActive(subItem));
        }
        return false;
    };

    useEffect(() => {
        const fetchSearchResults = async () => {
            if (searchQuery.length > 0) {
                try {
                    const response = await searchProducts(searchQuery);
                    setSearchResults(response.data);
                    setShowResults(true);
                } catch (error) {
                    console.error('Search error:', error);
                }
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        };

        fetchSearchResults();
    }, [searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleResultClick = () => {
        setShowResults(false);
    };

    const handleMobileSearch = (e) => {
        e.preventDefault();
        setShowResults(true);
    };

    const getValidPhoto = (result) => {
        if (result.product_option && result.product_option.length > 0) {
            const optionPhoto = result.product_option.find(option => option.url);
            if (optionPhoto) {
                return optionPhoto.url;
            }
        }
        if (result.photos && result.photos.length > 0) {
            return result.photos[0].src;
        }
        return null;
    };

    const renderMenuItems = (items) => {
        return items?.map(item => (
            <li className={`menu-icon ${isActive(item) ? 'active' : ''}`} key={item.title}>
                {item?.link ? (
                    <Link className={`${isActive(item) ? 'ltn__secondary-color' : ''}`} to={item.link}>{item.title}</Link>
                ) : (
                    <Link className={`${isActive(item) ? 'ltn__secondary-color' : ''}`} to={item.link}>{item.title}</Link>
                )}
                {item.subMenu && (
                    <ul className={item.subMenuClass}>
                        {renderMenuItems(item.subMenu)}
                    </ul>
                )}
            </li>
        ));
    };

    const history = useHistory();


    const handleSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim() !== "") {
            setShowResults(false);
            history.push(`${all_routes.product}?query=${searchQuery}`);
        }
    };

    return (
        <div>
            <header className={"shadow-md border-bottom border-light ltn__header-area ltn__header-5 header"}>
                <div className="ltn__header-middle-area ltn__header-sticky ltn__sticky-bg-white">
                    <div className="container">
                        <div className="row" style={{ justifyContent: 'space-between' }}>
                            <div className="col logoContainer" style={{ flex: 'none', width: 'fit-content' }}>
                                <Logo svgContent={svgContent} />
                            </div>
                            <div className="w-auto header-menu-column">
                                <div className="header-menu d-none d-xl-block go-top">
                                    <nav>
                                        <div className="ltn__main-menu d-flex justify-content-center">
                                            <ul>
                                                {renderMenuItems(menuData)}
                                            </ul>
                                        </div>
                                    </nav>
                                </div>
                            </div>
                            <div ref={searchContainerRef} className={`col ${"searchContainer"}`} style={{ flex: 'none', width: 'fit-content', position: 'relative' }}>
                                <div className="header-search-2 d-none d-xl-block">
                                    <form onSubmit={handleSubmit}>
                                        <input
                                            type="text"
                                            name="search"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setShowResults(true);
                                            }}
                                            onClick={() => setShowResults(true)}
                                            placeholder="Busca Aquí"
                                        />
                                        <button type="submit">
                                            <Link  to={`${all_routes.product}?query=${searchQuery}`}><i className="icon-search" /></Link>
                                        </button>
                                    </form>
                                    {showResults && searchResults.length > 0 && (
                                        <ul className="search-results scroll-light" style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                            {searchResults.map(result => {
                                                const photoUrl = getValidPhoto(result);
                                                return (
                                                    <li key={result.id} style={{ padding: '10px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Link to={`${all_routes.productDetails}/${result.id}`} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', width: '100%' }} onClick={handleResultClick}>
                                                            {photoUrl && (
                                                                <ImageWithBaseApi
                                                                    loadingIconFontSize="10"
                                                                    src={photoUrl}
                                                                    alt={result.title}
                                                                    style={{
                                                                        width: '60px',
                                                                        height: '60px',
                                                                        marginRight: '10px',
                                                                        objectFit: 'cover',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        textAlign: 'center'
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <h6 className="mb-0" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{result.title}</h6>
                                                                <p className="mb-0" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{result.description}</p>
                                                            </div>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                                <div className="mobile-menu-toggle me-0 d-xl-none">
                                    <MobileMenuButton
                                        isOpen={isMobileMenuOpen}
                                        onClick={toggleMobileMenu}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <div style={{ height: '80px' }}></div>
            <MobileMenu
                isOpen={isMobileMenuOpen}
                onLinkClick={toggleMobileMenu}
                menuItems={menuData}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleMobileSearch={handleMobileSearch}
                searchResults={searchResults}
                showResults={showResults}
                handleResultClick={handleResultClick}
            />
        </div>
    );
};

export default NavbarArtectum;